import {
  buildForm,
  buildHtml,
  buildLink,
  KitchenSinkRoute,
  RouteSlotInput,
} from '@luks/ui';
import { memoize } from 'lodash';

import {
  ListingMedicineNewsFiltersInput,
  ListingMedicineNewsQuery,
  ListingMedicineNewsQueryVariables,
  LiveMedicineNewsFragment,
  useInfiniteListingMedicineNewsQuery,
} from '../../../generated/queries';
import { formNavigate, useQueryParameters } from '../../commons/forms';
import { isDefined } from '../../commons/is-defined';
import { Complete, InferFormValues } from '../../commons/types';

type Slot = RouteSlotInput<typeof KitchenSinkRoute, 'body', 'MedicinesList'>;

type SlotInput = Exclude<Slot['input'], Function>;

type FormValues<TFormProp extends keyof SlotInput> = InferFormValues<
  SlotInput[TFormProp]
>;

type MedicineNewsItem = SlotInput['medicines'][number];

const aggregateResults = (pages?: Array<ListingMedicineNewsQuery>) =>
  pages?.reduce(
    (acc, value) => [
      ...acc,
      ...value.listingMedicineNews.items.filter(isDefined).map(mapNewsItem),
    ],
    [] as Array<MedicineNewsItem>,
  ) || [];

const mapNewsItem = memoize(
  (item: LiveMedicineNewsFragment): MedicineNewsItem => {
    return {
      date: new Date(item.date),
      left: {
        name: item.oldName,
        change: item.oldChange,
        composition: item.oldComposition,
        articleNumber: item.oldArticleNumber?.toString(),
        comment: item.oldComment,
      },
      right: {
        name: item.newName,
        change: item.newChange,
        composition: item.newComposition,
        articleNumber: item.newArticleNumber?.toString(),
        comment: item.newComment,
      },
      Description: item.commentHtml ? buildHtml(item.commentHtml) : undefined,
    };
  },
  (item) => `medicine-news-${item.id}`,
);

const FiltersForm = buildForm<FormValues<'Form'>>({
  initialValues: {
    text: '',
    change: '',
  },
  onSubmit: formNavigate(),
  onReset: formNavigate(),
  useInitialValues: useQueryParameters,
});

export function mapMedicineNews(
  input: ParagraphListingMedicineNewsFragment,
  context: { items: Array<any>; index: number },
): Slot {
  return {
    key: 'MedicinesList',
    input: function useListingMedicineNews() {
      const pageSize = 12;

      const params = useQueryParameters<FormValues<'Form'>>();
      const filters: ListingMedicineNewsFiltersInput = {
        text: params?.text,
        change: params?.change,
      };

      const variables: ListingMedicineNewsQueryVariables = {
        argument: input.argument || '',
        offset: 0,
        limit: pageSize,
        filters,
      };

      const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
      } = useInfiniteListingMedicineNewsQuery(variables, {
        keepPreviousData: true,
        getNextPageParam: (lastPage, allPages) => {
          const displayedItems = allPages.reduce(
            (acc, val) => acc + (val.listingMedicineNews.items.length || 0),
            0,
          );
          if (lastPage.listingMedicineNews.total <= displayedItems) {
            return undefined;
          }
          return {
            ...variables,
            offset: displayedItems,
          };
        },
      });

      const latest = data?.pages.at(-1);

      const props: Complete<SlotInput> = {
        title: input.title,
        medicines: aggregateResults(data?.pages),
        changes: input.medicineFilters?.change.map(mapItem) || [],
        Form: FiltersForm,
        infiniteScroll: context.index === context.items.length - 1,
        MoreLink: hasNextPage
          ? buildLink({
              onClick: (ev) => {
                ev.preventDefault();
                fetchNextPage();
              },
            })
          : undefined,
      };

      const status = isFetchingNextPage
        ? 102
        : isLoading || isFetching
        ? 101
        : data
        ? (latest?.listingMedicineNews.total || 0) > 0
          ? 200
          : 404
        : 0;

      return [props, status];
    },
  };
}

const mapItem = (item: {
  id: string;
  title: string;
}): { value: string; title: string } => ({
  value: item.id,
  title: item.title,
});
